import { atom } from "jotai";
import { MapAtom } from "types/maps/entities";

import { EntityKind } from "../../../models/entityKind";

/**
 * Facilities Map Atom Interface
 */
export interface FacilitiesMapAtom extends MapAtom {
  entityKind: EntityKind.Facility;
}

/**
 * Empty facilities map state
 */
export const EMPTY_FACILITIES_MAP: FacilitiesMapAtom = {
  id: "",
  legend: {
    legendItems: [],
    title: ""
  },
  entityKind: EntityKind.Facility
};

/**
 * Base atom for facilities map data
 * Writable atom that automatically sets entityKind to Facility
 */
const facilitiesMapBaseAtom = atom<FacilitiesMapAtom>(EMPTY_FACILITIES_MAP);

export const facilitiesMapAtom = atom(
  (get) => get(facilitiesMapBaseAtom),
  (get, set, update: Omit<FacilitiesMapAtom, "entityKind">) => {
    set(facilitiesMapBaseAtom, { ...update, entityKind: EntityKind.Facility });
  }
);

// ============================================================================
// Derived Atoms - Facilities Map Properties
// ============================================================================

/**
 * Facilities map transaction ID
 */
export const facilitiesTransactionIdAtom = atom((get) => get(facilitiesMapAtom).id);

/**
 * Facilities legend items
 */
export const facilitiesLegendItemsAtom = atom(
  (get) => get(facilitiesMapAtom).legend.legendItems
);

/**
 * Facilities legend title
 */
export const facilitiesLegendTitleAtom = atom(
  (get) => get(facilitiesMapAtom).legend.title
);

/**
 * Complete facilities legend object
 */
export const facilitiesLegendAtom = atom((get) => get(facilitiesMapAtom).legend);

/**
 * Total count of facility legend items
 */
export const totalFacilityCountAtom = atom(
  (get) => get(facilitiesMapAtom).legend.legendItems.length
);

/**
 * Total count of facilities
 */
export const totalFacilitiesCountAtom = atom((get) => {
  const legendItems = get(facilitiesMapAtom).legend.legendItems;
  return legendItems
    .filter((item) => item.inFilter)
    .reduce((sum, item) => sum + (item.count || 0), 0);
});

/**
 * Check if facilities data is loaded
 */
export const isFacilitiesDataLoadedAtom = atom((get) => {
  const facilitiesMap = get(facilitiesMapAtom);
  return facilitiesMap.id !== "" && facilitiesMap.legend.legendItems.length > 0;
});
